import React, { useCallback, useEffect, useState } from "react";
import { replace, useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Container,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Phone, Refresh } from "@mui/icons-material";
import { useFirebaseAuth } from "../hooks/useFirebaseAuth";
import axios from "axios";
import { useToast } from "./Toast";
import { createSession } from "../utils/sessionManager";
import { UserScope } from "../types/operationType";
import { PROJECT_TERMINOLOGY } from "../project/project_module/enums";
import {
  ConfirmationResult,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  AuthError,
} from "firebase/auth";
import { auth, appCheckEnabled } from "../firebase/config";

const StyledForm = styled("form")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  width: "100%",
}));

const OTPInput = styled(TextField)({
  "& input": {
    letterSpacing: "0.5em",
    textAlign: "center",
  },
});

const IconWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(2),
  "& svg": {
    fontSize: "1.5rem",
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
}));

const getErrorMessage = (error: AuthError): string => {
  switch (error.code) {
    case "auth/invalid-phone-number":
      return "Invalid phone number format";
    case "auth/too-many-requests":
      return "Too many attempts. Please try again later";
    case "auth/code-expired":
      return "Verification code has expired. Please request a new one";
    case "auth/invalid-verification-code":
      return "Invalid verification code";
    case "auth/network-request-failed":
      return "Network error. Please check your connection";
    case "auth/internal-error":
      return "Service temporarily unavailable. Please try again";
    case "auth/timeout":
      return "Request timed out. Please try again";
    case "auth/argument-error":
      return "Phone verification system not ready. Please ensure App Check with Turnstile is properly configured.";
    default:
      return error.message || "An unexpected error occurred";
  }
};
const OTPLogin: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmationResult, setConfirmationResult] =
    useState<ConfirmationResult | null>(null);
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [recaptchaVerifier, setRecaptchaVerifier] =
    useState<RecaptchaVerifier | null>(null);
  const [isSystemReady, setIsSystemReady] = useState(false);
  const [systemCheckError, setSystemCheckError] = useState<string | null>(null);
  const [isTurnstileAvailable, setIsTurnstileAvailable] = useState<boolean | null>(null);

  // Initialize authentication system with Turnstile fallback support
  useEffect(() => {
    const initializeAuthSystem = async () => {
      try {
        console.log("🔄 [DEBUG] Initializing authentication system...");
        
        // Check if Turnstile is available
        const turnstileAvailable = await checkTurnstileAvailability();
        setIsTurnstileAvailable(turnstileAvailable);

        if (turnstileAvailable) {
          console.log("✅ [DEBUG] Using Turnstile for verification");
        } else {
          console.log("⚠️ [DEBUG] Turnstile unavailable, will use reCAPTCHA v2 fallback");
        }

        // Initialize RecaptchaVerifier (needed regardless of Turnstile availability)
        if (!recaptchaVerifier) {
          let container = document.getElementById("recaptcha-container");
          if (!container) {
            container = document.createElement("div");
            container.id = "recaptcha-container";
            
            if (turnstileAvailable) {
              // Hide container for Turnstile mode
              container.style.display = "none";
              container.style.visibility = "hidden";
              container.style.position = "absolute";
              container.style.left = "-9999px";
            } else {
              // Make container visible for reCAPTCHA v2 fallback
              container.style.display = "block";
              container.style.margin = "10px 0";
            }
            
            document.body.appendChild(container);
          }

          const verifier = new RecaptchaVerifier(auth, "recaptcha-container", {
            size: turnstileAvailable ? "invisible" : "normal", // Use normal size for fallback
            callback: () => {
              console.log("✅ [DEBUG] RecaptchaVerifier callback triggered");
            },
            "expired-callback": () => {
              console.log("⚠️ [DEBUG] RecaptchaVerifier expired");
            },
          });

          setRecaptchaVerifier(verifier);
        }

        console.log("✅ [DEBUG] Authentication system ready");
        setIsSystemReady(true);
        setSystemCheckError(null);
      } catch (error: any) {
        console.error("❌ [DEBUG] Failed to initialize authentication system:", error);
        setSystemCheckError("Failed to initialize secure authentication. Please refresh the page and try again.");
        setIsSystemReady(false);
      }
    };

    initializeAuthSystem();
  }, []); // Run only once on mount

  const getMinimalRecaptchaVerifier = useCallback(() => {
    if (recaptchaVerifier) {
      return recaptchaVerifier;
    }

    // Fallback: create verifier on demand
    console.warn("⚠️ [DEBUG] RecaptchaVerifier not pre-initialized, creating on demand");
    
    let container = document.getElementById("recaptcha-container");
    if (!container) {
      container = document.createElement("div");
      container.id = "recaptcha-container";
      container.style.display = "none";
      container.style.visibility = "hidden";
      container.style.position = "absolute";
      container.style.left = "-9999px";
      document.body.appendChild(container);
    }

    const verifier = new RecaptchaVerifier(auth, "recaptcha-container", {
      size: "invisible",
      callback: () => {
        console.log("✅ [DEBUG] Fallback RecaptchaVerifier callback triggered");
      },
    });
    
    setRecaptchaVerifier(verifier);
    return verifier;
  }, [recaptchaVerifier]);

  const checkTurnstileAvailability = useCallback(async () => {
    console.log("🔄 [DEBUG] Checking Turnstile availability...");
    
    // Wait for Turnstile script to load (reasonable timeout)
    let scriptAttempts = 0;
    const maxScriptAttempts = 10; // 5 seconds max wait for script

    while (
      scriptAttempts < maxScriptAttempts &&
      typeof (window as any).turnstile?.getResponse !== "function"
    ) {
      console.log(`🔄 [DEBUG] Waiting for Turnstile script... attempt ${scriptAttempts + 1}`);
      await new Promise((resolve) => setTimeout(resolve, 500));
      scriptAttempts++;
    }

    // Check if Turnstile loaded successfully
    if (typeof (window as any).turnstile?.getResponse === "function") {
      console.log("✅ [DEBUG] Turnstile is available and functional");
      
      // Additional checks for widget container
      const turnstileContainer = document.getElementById("turnstile-widget");
      if (turnstileContainer) {
        console.log("✅ [DEBUG] Turnstile widget container found");
      } else {
        console.log("⚠️ [DEBUG] Turnstile widget container not found, but API is available");
      }
      
      // Small buffer to ensure everything is settled
      await new Promise((resolve) => setTimeout(resolve, 500));
      return true;
    } else {
      console.log("⚠️ [DEBUG] Turnstile not available, will fall back to reCAPTCHA v2");
      return false;
    }
  }, []);

  const cleanupRecaptchaVerifier = useCallback(() => {
    if (recaptchaVerifier) {
      try {
        recaptchaVerifier.clear();
        setRecaptchaVerifier(null);
        
        // Remove the container
        const container = document.getElementById("recaptcha-container");
        if (container) {
          container.remove();
        }
      } catch (error) {
        console.warn("Error cleaning up RecaptchaVerifier:", error);
      }
    }
  }, [recaptchaVerifier]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupRecaptchaVerifier();
    };
  }, [cleanupRecaptchaVerifier]);

  const { user } = useFirebaseAuth();

  useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 10) {
      setPhoneNumber(value);
    }
  };

  const handleOTPChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 6) {
      setOtp(value);
    }
  };

  const sendOTP = async (phoneNumber: string) => {
    try {
      setLoading(true);
      setError(null);

      // Phone number formatting
      let formattedPhoneNumber = phoneNumber;
      if (!phoneNumber.startsWith("+")) {
        formattedPhoneNumber = `+91${phoneNumber}`;
      }

      // Phone number validation
      if (!/^\+91[1-9]\d{9}$/.test(formattedPhoneNumber)) {
        throw new Error("Please enter a valid 10-digit mobile number");
      }

      // Get RecaptchaVerifier (guaranteed to be ready since UI is enabled)
      const verifier = getMinimalRecaptchaVerifier();

      console.log(`🔄 [DEBUG] Sending OTP (${isTurnstileAvailable ? 'Turnstile' : 'reCAPTCHA v2'} mode)...`);
      
      // Send OTP - Either Turnstile or reCAPTCHA v2 will handle verification
      const confirmation = await signInWithPhoneNumber(
        auth,
        formattedPhoneNumber,
        verifier
      );
      console.log("✅ [DEBUG] OTP sent successfully");
      setConfirmationResult(confirmation);
      setVerificationId(confirmation.verificationId);
      setError(null);
    } catch (err: any) {
      // Handle specific Firebase Auth errors
      if (err.code === "auth/app-not-authorized") {
        setError(
          "App is not authorized for this operation. Please check your Firebase App Check configuration."
        );
      } else if (err.code === "auth/web-context-cancelled") {
        setError("Verification was cancelled. Please try again.");
      } else if (err.code === "auth/quota-exceeded") {
        setError("Too many verification attempts. Please try again later.");
      } else if (err.code === "auth/network-request-failed") {
        setError(
          "Network error occurred. Please check your internet connection and App Check configuration."
        );
      } else if (err.code === "auth/argument-error") {
        setError(
          "Phone verification system not ready. Please ensure App Check with Turnstile is properly configured."
        );
      } else {
        const errorMessage = err.code
          ? getErrorMessage(err)
          : err.message || "Failed to send verification code";
        setError(errorMessage);
      }

      throw new Error(err.message || "Failed to send verification code");
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async (otp: string) => {
    try {
      setLoading(true);
      setError(null);

      if (!confirmationResult) {
        throw new Error("Please request a new OTP");
      }

      if (!/^\d{6}$/.test(otp)) {
        throw new Error("Please enter a valid 6-digit OTP");
      }

      const result = await confirmationResult.confirm(otp);

      // Clean up reCAPTCHA verifier immediately after successful verification
      cleanupRecaptchaVerifier();

      return result;
    } catch (err: any) {
      console.error("Firebase Auth Error:", err);
      const errorMessage = err.code
        ? getErrorMessage(err)
        : err.message || "Invalid OTP";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Show immediate feedback
      showToast("Sending OTP...", "info");
      await sendOTP(phoneNumber);
      setIsOtpSent(true);
      showToast("OTP sent successfully!", "success");
    } catch (err: any) {
      // Handle specific error types with user-friendly messages
      if (
        err.message?.includes("verification system") ||
        err.message?.includes("app-not-authorized") ||
        err.message?.includes("quota-exceeded")
      ) {
        showToast(
          "We're having trouble sending your otp. Please wait a moment and try again.",
          "warning"
        );
      } else if (err.message?.includes("network-request-failed")) {
        showToast(
          "Please check your internet connection and try again.",
          "error"
        );
      } else if (err.message?.includes("too-many-requests")) {
        showToast(
          "Too many attempts. Please wait a few minutes before trying again.",
          "warning"
        );
      } else if (err.message?.includes("invalid-phone-number")) {
        showToast("Please enter a valid 10-digit mobile number.", "error");
      } else {
        showToast(
          err.message || "Failed to send OTP. Please try again.",
          "error"
        );
      }
    }
  };

  const handleRetry = async () => {
    try {
      setIsOtpSent(false);
      setOtp("");
      setError(null);
    } catch (err) {
      console.error("Error retrying:", err);
      showToast(
        "Unable to reset. Please refresh the page and try again.",
        "error"
      );
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const result = await verifyOTP(otp);
      if (result.user) {
        // Get the Firebase ID token
        const token = await result.user.getIdToken();

        // Call complete-profile API
        try {
          const response = await axios.post(
            `${process.env.REACT_APP_API_HOST}users/complete-profile`,
            {
              phoneNumber: phoneNumber,
              firebaseUid: result.user.uid,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (response?.data?.isSuccess) {
            const userData = {
              ...response?.data?.data,
              accessToken: token,
            };

            sessionStorage.setItem("user", JSON.stringify(userData));
            sessionStorage.setItem("accessToken", token);

            createSession();
            if (userData?.userRole?.scope === UserScope.PROJECT) {
              navigate(`/${PROJECT_TERMINOLOGY.LOWER_PLURAL}`, {
                replace: true,
              });
            } else if (
              userData?.company?.verificationStatus === "PENDING" ||
              userData?.company?.hasVehicles === false
            ) {
              navigate("/profile", { replace: true });
            } else {
              showToast("Logged in successfully", "success");
              navigate("/dashboard", { replace: true });
            }
          } else if (!response.data?.isSuccess && response?.status === 404) {
            showToast("Please complete your profile to continue", "info");
            navigate("/profile", { replace: true });
          } else {
            throw new Error("Failed to login");
          }
        } catch (error) {
          console.error("Error completing profile:", error);
          if (axios.isAxiosError(error) && error.response?.status === 401) {
            // showToast("You are not registered with us.", "info");
            // navigate("/not-registered", { replace: true });
            navigate("/profile", { replace: true });
          } else {
            showToast("Failed to login. Please try again.", "error");
          }
        }
      }
    } catch (err: any) {
      console.error("Error verifying OTP:", err);

      // Handle OTP verification errors with user-friendly messages
      if (err.message?.includes("invalid-verification-code")) {
        showToast(
          "The otp you entered is incorrect. Please try again.",
          "error"
        );
      } else if (err.message?.includes("code-expired")) {
        showToast("Your otp has expired. Please request a new one.", "warning");
        setIsOtpSent(false);
        setOtp("");
      } else if (err.message?.includes("network-request-failed")) {
        showToast(
          "Please check your internet connection and try again.",
          "error"
        );
      } else {
        showToast("Unable to verify your otp. Please try again.", "error");
      }
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
        }}
      >
        {systemCheckError && (
          <Alert
            severity="error"
            sx={{ mb: 2, width: "100%" }}
          >
            {systemCheckError}
          </Alert>
        )}

        {!isSystemReady && !systemCheckError && (
          <Alert
            severity="info"
            sx={{ mb: 2, width: "100%" }}
          >
            {isTurnstileAvailable === null 
              ? "Checking security verification system..." 
              : isTurnstileAvailable 
                ? "Loading Turnstile security verification..." 
                : "Loading reCAPTCHA verification (Turnstile unavailable)..."
            }
          </Alert>
        )}

        {isSystemReady && isTurnstileAvailable === false && (
          <Alert
            severity="warning"
            sx={{ mb: 2, width: "100%" }}
          >
            Using reCAPTCHA verification. Please complete the challenge below when prompted.
          </Alert>
        )}

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2, width: "100%" }}
            action={
              <IconButton
                aria-label="retry"
                color="inherit"
                size="small"
                onClick={handleRetry}
                disabled={loading}
              >
                <Refresh />
              </IconButton>
            }
          >
            {error}
          </Alert>
        )}

        {!isOtpSent ? (
          <StyledForm onSubmit={handleSendOTP}>
            <IconWrapper>
              <Phone />
              <Typography variant="body1">Login with Mobile Number</Typography>
            </IconWrapper>

            <TextField
              fullWidth
              variant="outlined"
              value={phoneNumber}
              onChange={handlePhoneNumberChange}
              placeholder="Enter 10-digit mobile number"
              inputProps={{
                maxLength: 10,
                pattern: "[0-9]*",
                inputMode: "numeric",
              }}
              required
              disabled={loading || !isSystemReady}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={phoneNumber.length !== 10 || loading || !isSystemReady}
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : !isSystemReady ? (
                isTurnstileAvailable === null ? "Checking..." : 
                isTurnstileAvailable ? "Loading Turnstile..." : "Loading reCAPTCHA..."
              ) : (
                "Send OTP"
              )}
            </Button>
          </StyledForm>
        ) : (
          <StyledForm onSubmit={handleVerifyOTP}>
            <Typography variant="body1" gutterBottom>
              Enter verification code sent to {phoneNumber}
            </Typography>
            <OTPInput
              fullWidth
              variant="outlined"
              value={otp}
              onChange={handleOTPChange}
              placeholder="Enter 6-digit code"
              inputProps={{
                maxLength: 6,
                pattern: "[0-9]*",
                inputMode: "numeric",
              }}
              required
              disabled={loading}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={otp.length !== 6 || loading}
            >
              {loading ? <CircularProgress size={24} /> : "Verify & Continue"}
            </Button>
            <Button
              variant="text"
              onClick={() => {
                setIsOtpSent(false);
                setOtp("");
              }}
              fullWidth
              disabled={loading}
            >
              Change Number
            </Button>
          </StyledForm>
        )}
      </Box>
    </Container>
  );
};

export default OTPLogin;
